<template>
  <div class="space-y-6">
    <h1 class="text-3xl font-bold">Отчеты и статистика</h1>

    <!-- Выбор периода -->
    <div class="bg-white p-4 rounded-lg shadow">
      <div class="grid grid-cols-3 gap-4">
        <div>
          <label class="block text-sm font-bold mb-2">От даты</label>
          <input
            v-model="filters.fromDate"
            type="date"
            class="w-full border px-3 py-2 rounded"
          />
        </div>
        <div>
          <label class="block text-sm font-bold mb-2">До даты</label>
          <input
            v-model="filters.toDate"
            type="date"
            class="w-full border px-3 py-2 rounded"
          />
        </div>
        <div class="flex items-end">
          <button
            @click="loadReports"
            class="w-full bg-blue-600 text-white py-2 rounded font-bold hover:bg-blue-700"
          >
            📊 Обновить
          </button>
        </div>
      </div>
    </div>

    <!-- KPI карточки -->
    <div class="grid grid-cols-4 gap-6">
      <div class="bg-white p-6 rounded-lg shadow">
        <p class="text-gray-600 text-sm">Заказов</p>
        <p class="text-4xl font-bold">{{ reports.totalOrders }}</p>
        <p class="text-xs text-green-600 mt-2">↑ {{ reports.ordersGrowth }}%</p>
      </div>
      <div class="bg-white p-6 rounded-lg shadow">
        <p class="text-gray-600 text-sm">Доход</p>
        <p class="text-4xl font-bold">₴{{ reports.totalRevenue }}</p>
        <p class="text-xs text-green-600 mt-2">↑ {{ reports.revenueGrowth }}%</p>
      </div>
      <div class="bg-white p-6 rounded-lg shadow">
        <p class="text-gray-600 text-sm">Средняя сумма</p>
        <p class="text-4xl font-bold">₴{{ reports.averageOrderValue }}</p>
      </div>
      <div class="bg-white p-6 rounded-lg shadow">
        <p class="text-gray-600 text-sm">Успешных заказов</p>
        <p class="text-4xl font-bold">{{ reports.completedOrders }}</p>
        <p class="text-xs text-blue-600 mt-2">{{ reports.successRate }}%</p>
      </div>
    </div>

    <!-- Статус заказов -->
    <div class="grid grid-cols-2 gap-6">
      <div class="bg-white p-6 rounded-lg shadow">
        <h2 class="text-xl font-bold mb-4">📊 Распределение по статусам</h2>
        <div class="space-y-4">
          <div v-for="(count, status) in reports.ordersByStatus" :key="status" class="flex justify-between items-center">
            <span class="text-gray-600">{{ getStatusLabel(status) }}</span>
            <div class="flex items-center gap-2 flex-1 ml-4">
              <div class="w-32 bg-gray-200 rounded-full h-3">
                <div
                  :style="{ width: (count / reports.totalOrders * 100) + '%' }"
                  :class="getStatusBarClass(status)"
                  class="h-3 rounded-full"
                ></div>
              </div>
              <span class="font-bold w-12 text-right">{{ count }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Платежи -->
      <div class="bg-white p-6 rounded-lg shadow">
        <h2 class="text-xl font-bold mb-4">💳 Статус платежей</h2>
        <div class="space-y-4">
          <div v-for="(count, status) in reports.paymentsByStatus" :key="status" class="flex justify-between items-center">
            <span class="text-gray-600">{{ getPaymentLabel(status) }}</span>
            <div class="flex items-center gap-2 flex-1 ml-4">
              <div class="w-32 bg-gray-200 rounded-full h-3">
                <div
                  :style="{ width: (count / reports.totalOrders * 100) + '%' }"
                  :class="getPaymentBarClass(status)"
                  class="h-3 rounded-full"
                ></div>
              </div>
              <span class="font-bold w-12 text-right">{{ count }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Популярные услуги -->
    <div class="bg-white p-6 rounded-lg shadow">
      <h2 class="text-xl font-bold mb-4">🏆 ТОП 5 услуг по спросу</h2>
      <table class="w-full">
        <thead class="bg-gray-50 border-b">
          <tr>
            <th class="px-6 py-3 text-left text-sm font-bold">Услуга</th>
            <th class="px-6 py-3 text-right text-sm font-bold">Заказов</th>
            <th class="px-6 py-3 text-right text-sm font-bold">Доход</th>
            <th class="px-6 py-3 text-right text-sm font-bold">Процент</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          <tr v-for="(service, idx) in reports.topServices" :key="idx">
            <td class="px-6 py-4 font-bold">{{ service.name }}</td>
            <td class="px-6 py-4 text-right">{{ service.count }}</td>
            <td class="px-6 py-4 text-right font-bold">₴{{ service.revenue }}</td>
            <td class="px-6 py-4 text-right">{{ service.percentage }}%</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Экспорт -->
    <div class="bg-white p-6 rounded-lg shadow">
      <h2 class="text-xl font-bold mb-4">📥 Экспорт отчета</h2>
      <div class="flex gap-2">
        <button
          @click="exportPDF"
          class="bg-red-600 text-white px-6 py-2 rounded font-bold hover:bg-red-700"
        >
          📄 Скачать PDF
        </button>
        <button
          @click="exportExcel"
          class="bg-green-600 text-white px-6 py-2 rounded font-bold hover:bg-green-700"
        >
          📊 Скачать Excel
        </button>
        <button
          @click="printReport"
          class="bg-blue-600 text-white px-6 py-2 rounded font-bold hover:bg-blue-700"
        >
          🖨️ Печать
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';

export default {
  name: 'AdminReports',
  setup() {
    const filters = ref({
      fromDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0]
    });

    const reports = ref({
      totalOrders: 145,
      totalRevenue: 28500,
      averageOrderValue: 196,
      completedOrders: 128,
      successRate: 88,
      ordersGrowth: 12,
      revenueGrowth: 18,
      ordersByStatus: {
        pending: 8,
        accepted: 12,
        'in-progress': 5,
        completed: 128,
        cancelled: 2
      },
      paymentsByStatus: {
        unpaid: 15,
        paid: 130,
        partial: 0
      },
      topServices: [
        { name: 'Замена экрана', count: 52, revenue: 10400, percentage: 36 },
        { name: 'Замена батареи', count: 38, revenue: 7600, percentage: 27 },
        { name: 'Ремонт ПО', count: 28, revenue: 5600, percentage: 20 },
        { name: 'Восстановление после воды', count: 18, revenue: 3600, percentage: 13 },
        { name: 'Другое', count: 9, revenue: 1300, percentage: 4 }
      ]
    });

    const statusLabels = {
      pending: 'Ожидание',
      accepted: 'Принят',
      'in-progress': 'В работе',
      completed: 'Завершен',
      cancelled: 'Отменен'
    };

    const paymentStatusLabels = {
      unpaid: 'Не оплачен',
      paid: 'Оплачен',
      partial: 'Частично'
    };

    const loadReports = () => {
      // TODO: Загрузить реальные данные с бэка
      console.log('Загрузка отчетов за период:', filters.value);
    };

    const getStatusLabel = (status) => statusLabels[status] || status;
    const getStatusBarClass = (status) => {
      const classes = {
        pending: 'bg-yellow-500',
        accepted: 'bg-blue-500',
        'in-progress': 'bg-purple-500',
        completed: 'bg-green-500',
        cancelled: 'bg-red-500'
      };
      return classes[status] || 'bg-gray-500';
    };

    const getPaymentLabel = (status) => paymentStatusLabels[status] || status;
    const getPaymentBarClass = (status) => {
      const classes = {
        unpaid: 'bg-red-500',
        paid: 'bg-green-500',
        partial: 'bg-yellow-500'
      };
      return classes[status] || 'bg-gray-500';
    };

    const exportPDF = () => {
      alert('Функция экспорта в PDF будет реализована');
      // TODO: Реализовать экспорт в PDF
    };

    const exportExcel = () => {
      alert('Функция экспорта в Excel будет реализована');
      // TODO: Реализовать экспорт в Excel
    };

    const printReport = () => {
      window.print();
    };

    onMounted(() => {
      loadReports();
    });

    return {
      filters,
      reports,
      loadReports,
      getStatusLabel,
      getStatusBarClass,
      getPaymentLabel,
      getPaymentBarClass,
      exportPDF,
      exportExcel,
      printReport
    };
  }
};
</script>
